import { FC, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { IDisciplineViewerList } from 'Data/interfaces/FederatedViewer/IDisciplineViewerList';
import { IProperties } from 'Data/interfaces/FederatedViewer/IProperties';
import { BimSelection, BimTreeNode, BimViewerHandle } from 'Data/interfaces/BimViewer/IBimViewerPort';
import { Bim3DToolbar } from 'Pages/Viewer/CoordlyViewer/components/Bim3DToolbar/Bim3DToolbar';
import { Bim3DTools, useBim3DTools } from 'Pages/Viewer/CoordlyViewer/3DViewer/useBim3DTools';
import ButtonDrawer from 'Components/UI/ButtonDrawer';
import { Icon } from 'Components/UI';
import PropertiesDrawer from '../PropertiesDrawer';
import stylesDrawer from '../PropertiesDrawer/PropertiesDrawer.module.scss';
import { useFederatedIfcViewer } from './useFederatedIfcViewer';

const VIEWER_VERSION = 'v1.0.0';

interface IFederatedIfcViewerProps {
  urns: string[];
  viewerListDisciplines: IDisciplineViewerList[];
  constructionSiteId: number;
  drawerOpen?: boolean;
  onEngineError?(detail: { code: string; message: string }): void;
  onModelLoadFailed?(urn: string): void;
  onLoadingUrnChange?(urn: string | null): void;
  // `modelIndex` endereça o data model deste modelo no motor — é o que a árvore
  // usa para não ler a hierarquia (nem selecionar) do arquivo errado.
  onModelTree?(urn: string, tree: BimTreeNode[], modelIndex?: number): void;
  // Seleção crua do motor. As árvores vivem fora daqui (na lista de modelos) e
  // precisam ouvir a cena para desmarcar quando o 3D desmarca — clique no vazio,
  // Esc ou seleção que passou para outro modelo.
  onSelectionChanged?(selection: BimSelection): void;
  onHandleReady?(handle: BimViewerHandle | null): void;
}

// Viewer IFC federado (ifc-lite/WebGPU) — usado na sessão IFC no lugar do
// InternalViewer (Autodesk). Vários modelos na cena via a porta (addModel por urn).
export const FederatedIfcViewer: FC<IFederatedIfcViewerProps> = ({
  urns,
  viewerListDisciplines,
  constructionSiteId,
  drawerOpen,
  onEngineError,
  onModelLoadFailed,
  onLoadingUrnChange,
  onModelTree,
  onSelectionChanged,
  onHandleReady,
}) => {
  // Namespace `viewer`: são as mesmas chaves do botão do painel no InternalViewer.
  const { t } = useTranslation('viewer');
  const [viewerDiv, setViewerDiv] = useState<HTMLDivElement | null>(null);
  // O painel de propriedades vive aqui, e não na página, porque no caminho da
  // Autodesk quem o monta é o InternalViewer — que não existe na sessão IFC.
  const [selectedProperties, setSelectedProperties] = useState<IProperties | null>(null);
  const [propertiesVisible, setPropertiesVisible] = useState(false);
  const [sectionOpen, setSectionOpen] = useState(false);

  // Declarado antes do hook da cena: os callbacks abaixo são criados ali e só
  // disparam depois do render, quando `tools` já existe. É o mesmo laço que o
  // ViewerBody3D resolve assim — a cena precisa avisar as ferramentas, e as
  // ferramentas precisam do handle que a cena cria.
  const toolsRef = useRef<Bim3DTools | null>(null);

  const { loaded, phase, fitToView, handleRef } = useFederatedIfcViewer({
    viewerDiv,
    urns,
    viewerListDisciplines,
    constructionSiteId,
    onEngineError,
    onModelLoadFailed,
    onLoadingUrnChange,
    onModelTree,
    onHandleReady,
    // Selecionou um elemento na cena: abre o painel junto, senão a seleção não
    // teria efeito visível nenhum fora do destaque no 3D.
    onSelectedProperties: (properties) => {
      setSelectedProperties(properties);
      if (properties) { setPropertiesVisible(true); }
    },
    // Síncrono de propósito: as árvores reagem dentro do mesmo clique, e é o que
    // deixa o clique num nó contêiner (que limpa o destaque no 3D) marcar o nó
    // em vez de se apagar num render seguinte.
    onSelection: (selection) => {
      toolsRef.current?.handleSelectionChanged(selection);
      onSelectionChanged?.(selection);
    },
    onMeasure: (state) => toolsRef.current?.handleMeasureChanged(state),
  });

  // As ferramentas (isolar, ocultar, corte, medição, X-Ray) são as mesmas do
  // viewer único: o hook opera a cena pelos comandos da porta, então serve
  // qualquer cena — inclusive a federada, com vários modelos.
  const tools = useBim3DTools({ handleRef });
  toolsRef.current = tools;

  return (
    <>
      <Helmet>
        <link rel="stylesheet" type="text/css" href={`/coordly3DViewer/${VIEWER_VERSION}/css/loader.css`} />
      </Helmet>

      <div className={`app-shell embedded${drawerOpen ? ' drawer-open' : ''}`}>
        {phase !== 'idle' && (
          <div className="x-spinner-container">
            <div className="x-spinner"></div>
          </div>
        )}

        <main className="viewer-pane" style={{ position: 'relative', overflow: 'hidden' }}>
          <section className="viewer-canvas-area">
            <div id="bim-container" ref={setViewerDiv}></div>
          </section>

          {loaded && (
            <ButtonDrawer
              onClick={() => setPropertiesVisible((current) => !current)}
              style={{
                position: 'absolute',
                marginTop: 24,
                right: propertiesVisible ? 326 : 14,
                zIndex: 99,
              }}
              placement={propertiesVisible ? t('closePanel') : t('expandPanel')}
              border
              delay
            >
              <span className={stylesDrawer['iconToggleWrap']}>
                <Icon
                  icon="avancarDuplo"
                  customSize={10}
                  className={`${stylesDrawer['iconChildren']} ${propertiesVisible ? stylesDrawer['iconActive'] : ''}`}
                />
              </span>
            </ButtonDrawer>
          )}

          {propertiesVisible && (
            <PropertiesDrawer
              properties={selectedProperties}
              visible={propertiesVisible}
              onClose={() => setPropertiesVisible(false)}
              onShow={() => setPropertiesVisible(true)}
            />
          )}

          {/* Sem `treeOpen`/`onToggleTree`: no federado a árvore não é um painel
              do viewer, ela vive na lista de modelos (uma por modelo ligado). */}
          <Bim3DToolbar
            enabled={loaded}
            hasSelection={tools.hasSelection}
            selectionCount={tools.selectionCount}
            isolated={tools.isolated}
            multiSelect={tools.multiSelect}
            ghost={tools.ghost}
            onToggleMultiSelect={tools.toggleMultiSelect}
            onToggleGhost={tools.toggleGhost}
            propertiesOpen={propertiesVisible}
            sectionOpen={sectionOpen}
            section={tools.section}
            onFit={fitToView}
            onFitSelection={tools.frameSelection}
            onIsolate={tools.isolateSelection}
            onHide={tools.hideSelection}
            onShowAll={tools.showAll}
            onToggleProperties={() => setPropertiesVisible((current) => !current)}
            onToggleSection={() => setSectionOpen((current) => !current)}
            onSectionChange={tools.setSection}
            measureMode={tools.measureMode}
            measurements={tools.measurements}
            onToggleMeasure={tools.toggleMeasureMode}
            onClearMeasurements={tools.clearMeasurements}
          />
        </main>
      </div>
    </>
  );
};
